import { EMPTY_BILLING_CONFIG } from '../components/BillingConfigSection';
import { mapReceivableToPayload, mapDisbursementToPayload } from './dataMappers';
import { sanitizeFileName } from './operationHelpers';

interface ReceivablesAssignmentPayloadParams {
    form: {
        type: string;
        operation_number: string;
        originator: string;
        approved_on: string;
        loan_amount: number;
        assignment_type_name: string;
        with_recourse: boolean;
        credit_product_type_name: string;
        receiving_account_name?: string;
        expiration_date: string;
        guarantee_percentage: number;
        total_amount: number;
        emission_date: string;
        external_link: string;
        rebate_amount: number;
        sequencial_number: number;
        receivables: {
            quantity: number;
            total: number;
            items: any[];
            spreadsheet_processed?: boolean;
            overcollateral?: boolean | null;
        };
        disbursement: {
            disbursements: any[];
            totalAmount: number;
        };
        base_calculation?: {
            tir_annual: number | null;
            tir_monthly: number | null;
            spread_annual: number | null;
            vpl: number | null;
            vpl_margin: number | null;
            duration_days: number | null;
        };
        billing_config?: any;
    };
    creditOperationId?: string;
    selectedCedente: string;
    selectedDestinatario: string;
    assignorData: any;
    recipientData: any;
    contractFiles: File[];
    artifactFiles: File[];
    existingArtifactFiles: string[];
    removedArtifactFiles: string[];
    existingContractFile: string;
    removedContractFile: boolean;
}

export const buildReceivablesAssignmentPayload = ({
    form,
    creditOperationId,
    selectedCedente,
    selectedDestinatario,
    assignorData,
    recipientData,
    contractFiles,
    artifactFiles,
    existingArtifactFiles,
    removedArtifactFiles,
    existingContractFile,
    removedContractFile,
}: ReceivablesAssignmentPayloadParams) => ({
    type: form.type || "",
    assignor: selectedCedente || "00000000-0000-0000-0000-000000000000",
    recipient: recipientData?.id || "00000000-0000-0000-0000-000000000000",
    credit_operation_id: creditOperationId,
    operation_number: form.operation_number || "",
    originator: form.originator || "",
    operation_type: "RECEIVABLES_ASSIGNMENT",
    operation_schema_id: 2,
    approved_on: form.approved_on || null,
    approved_value: null,
    current_balance: null,
    debt_balance_id: null,
    nominal_value: form.disbursement.totalAmount || 0,
    loan_amount: form.loan_amount || 0,
    assignment_type_name: form.assignment_type_name || "",
    with_recourse: form.with_recourse,
    credit_product_type_name: form.credit_product_type_name || "",
    receiving_account_name: form.receiving_account_name || "",
    due_date: form.expiration_date || null,
    participants: [
        {
            id: assignorData?.id || null,
            name: assignorData?.name || '',
            document: assignorData?.document || "",
            is_active: true,
            person_id: selectedCedente || "00000000-0000-0000-0000-000000000000",
            is_primary: true,
            effective_to: null,
            effective_from: assignorData?.effective_from || new Date().toISOString(),
            guarantee_value: null,
            participant_type_id: 1,
            participation_order: 1,
            guarantee_percentage: form.guarantee_percentage,
            participant_type_name: "ASSIGNOR",
            receives_disbursement: false
        },
        {
            id: recipientData?.id || null,
            name: recipientData?.name || '',
            document: recipientData?.document || "",
            is_active: recipientData?.is_active || true,
            person_id: selectedDestinatario || "00000000-0000-0000-0000-000000000000",
            is_primary: recipientData?.is_primary || false,
            effective_to: recipientData?.effective_to || null,
            effective_from: recipientData?.effective_from || new Date().toISOString(),
            guarantee_value: null,
            participant_type_id: 5,
            participation_order: 1,
            guarantee_percentage: null,
            participant_type_name: "RECIPIENT",
            receives_disbursement: false
        }
    ],
    operation_dictionary: {
        receivables: {
            items: (form.receivables?.items || []).map(mapReceivableToPayload),
            total: form.receivables.total || 0,
            quantity: form.receivables.quantity || 1,
            spreadsheet_processed: form.receivables.spreadsheet_processed || false,
            overcollateral: form.receivables?.overcollateral ?? null
        },
        disbursement: {
            totalAmount: form.disbursement.totalAmount || null,
            disbursements: form.disbursement.disbursements.map(mapDisbursementToPayload)
        },
        total_amount: form.total_amount || null,
        emission_date: form.emission_date || null,
        external_link: form.external_link || "",
        rebate_amount: form.rebate_amount || null,
        expiration_date: form.expiration_date || null,
        operation_number: form.operation_number || "",
        guarantee_percentage: form.guarantee_percentage || null,
        base_calculation: form.base_calculation ? {
            tir_annual: form.base_calculation.tir_annual,
            tir_monthly: form.base_calculation.tir_monthly,
            spread_annual: form.base_calculation.spread_annual,
            vpl: form.base_calculation.vpl,
            vpl_margin: form.base_calculation.vpl_margin,
            duration_days: form.base_calculation.duration_days
        } : undefined,
        billing_config: form.billing_config || EMPTY_BILLING_CONFIG,
        files: {
            contract_file: contractFiles.length > 0
                ? `${form.sequencial_number}${sanitizeFileName(contractFiles[0].name.substring(contractFiles[0].name.lastIndexOf('.')))}`
                : (removedContractFile ? '' : existingContractFile),
            artefacts: [
                ...existingArtifactFiles.filter(file => !removedArtifactFiles.includes(file)),
                ...artifactFiles.map(file => sanitizeFileName(file.name))
            ]
        },
    }
});
